import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { apiPost, apiGet } from '@/utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  ArrowLeft, 
  Upload, 
  Scan, 
  Check, 
  Loader2,
  User,
  CreditCard,
  FileText,
  Camera,
  Trash2,
  Paperclip
} from 'lucide-react';
import { toast } from 'sonner';

const NewCasePage = () => {
  const navigate = useNavigate();
  const { suppliers, fetchSuppliers } = useApp();
  const fileInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const docsInputRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    supplierId: '',
    workerName: '',
    workerNameArabic: '',
    passportNumber: '',
    nationality: '',
    dateOfBirth: '',
    gender: '',
    profession: '',
    placeOfBirth: '',
    visaNumber: '',
    uidNumber: '',
    visaIssueDate: '',
    visaExpiryDate: '',
    visaIssuePlace: '',
    visaType: '',
    sponsorName: '',
    passportExpiry: '',
    totalCost: '',
    advanceAmount: '',
    notes: '',
    arrivalStatus: 'abroad'
  });

  const [scanStatus, setScanStatus] = useState('idle'); // idle, scanning, success, error
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoData, setPhotoData] = useState('');
  const [saving, setSaving] = useState(false);
  const [currency, setCurrency] = useState('AED');
  const [documents, setDocuments] = useState([]); // {fileName, mimeType, fileData}

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  // Update currency when supplier changes
  useEffect(() => {
    if (formData.supplierId) {
      const supplier = suppliers.find(s => s.id === formData.supplierId);
      if (supplier) {
        setCurrency(supplier.currency || 'AED');
      }
    }
  }, [formData.supplierId, suppliers]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // AI Visa Scan
  const handleVisaScan = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setScanStatus('scanning');

    try {
      // Convert to base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const response = await apiPost('/api/visas/scan', {
        file_base64: base64,
        file_mime_type: file.type || 'image/jpeg'
      });

      if (response?.success && response.extracted_data) {
        const data = response.extracted_data;
        
        setFormData(prev => ({
          ...prev,
          workerName: data.passenger_name || prev.workerName,
          workerNameArabic: data.passenger_name_arabic || prev.workerNameArabic,
          passportNumber: data.passport_number || prev.passportNumber,
          nationality: data.nationality || prev.nationality,
          dateOfBirth: data.date_of_birth || prev.dateOfBirth,
          placeOfBirth: data.place_of_birth || prev.placeOfBirth,
          gender: data.gender || prev.gender,
          profession: data.profession || prev.profession,
          visaNumber: data.visa_number || prev.visaNumber,
          uidNumber: data.uid_number || prev.uidNumber,
          visaIssueDate: data.visa_issue_date || prev.visaIssueDate,
          visaExpiryDate: data.visa_expiry_date || prev.visaExpiryDate,
          visaIssuePlace: data.visa_issue_place || prev.visaIssuePlace,
          visaType: data.visa_type || prev.visaType,
          sponsorName: data.sponsor_name || prev.sponsorName,
          passportExpiry: data.passport_expiry || prev.passportExpiry,
        }));

        setScanStatus('success');
        toast.success('Visa data extracted successfully!');
      } else {
        throw new Error('No data extracted');
      }
    } catch (error) {
      console.error('Visa scan error:', error);
      setScanStatus('error');
      toast.error('Failed to scan visa: ' + (error.message || 'Unknown error'));
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Photo upload
  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      setPhotoPreview(e.target.result);
      setPhotoData(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  // Documents (images + PDF) — collected locally, uploaded after the worker is created
  const handleDocsSelect = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    const added = [];
    for (const file of files) {
      const isValid = file.type.startsWith('image/') || file.type === 'application/pdf';
      if (!isValid) { toast.error(`${file.name}: صيغة غير مدعومة (صور و PDF فقط)`); continue; }
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name}: الحجم كبير جداً (الحد 10MB)`); continue; }
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      added.push({ fileName: file.name, mimeType: file.type, fileData: dataUrl });
    }
    setDocuments((prev) => [...prev, ...added]);
    if (docsInputRef.current) docsInputRef.current.value = '';
  };

  const removeDocument = (index) => {
    setDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  // Save case
  const handleSave = async () => {
    // Validation
    if (!formData.supplierId) {
      toast.error('Please select a supplier');
      return;
    }
    if (!formData.workerName.trim()) {
      toast.error('Please enter worker name');
      return;
    }
    if (!formData.nationality.trim()) {
      toast.error('Please enter nationality');
      return;
    }
    if (!formData.totalCost || parseFloat(formData.totalCost) <= 0) {
      toast.error('Please enter total cost');
      return;
    }

    const totalCost = parseFloat(formData.totalCost) || 0;
    const advanceAmount = parseFloat(formData.advanceAmount) || 0;

    if (advanceAmount > totalCost) {
      toast.error('Advance payment cannot exceed total cost');
      return;
    }

    setSaving(true);

    try {
      const caseData = {
        supplierId: formData.supplierId,
        workerName: formData.workerName.trim(),
        workerNameArabic: formData.workerNameArabic.trim(),
        nationality: formData.nationality.trim(),
        passportNumber: formData.passportNumber.trim(),
        totalCost,
        advanceAmount,
        paidAmount: advanceAmount,
        deductionsUSD: 0,
        deductionLog: [],
        remainingAmount: totalCost - advanceAmount,
        status: 'abroad',
        notes: formData.notes.trim(),
        photoData,
        arrivalStatus: formData.arrivalStatus,
        // Visa fields
        dateOfBirth: formData.dateOfBirth,
        placeOfBirth: formData.placeOfBirth,
        gender: formData.gender,
        profession: formData.profession,
        visaNumber: formData.visaNumber,
        uidNumber: formData.uidNumber,
        visaIssueDate: formData.visaIssueDate,
        visaExpiryDate: formData.visaExpiryDate,
        visaIssuePlace: formData.visaIssuePlace,
        visaType: formData.visaType,
        sponsorName: formData.sponsorName,
        passportExpiry: formData.passportExpiry,
      };

      const created = await apiPost('/api/cases', caseData);

      // Upload any attached documents to the freshly-created worker.
      if (created?.id && documents.length > 0) {
        let ok = 0;
        for (const d of documents) {
          try {
            await apiPost(`/api/cases/${created.id}/documents`, d);
            ok += 1;
          } catch (e) {
            toast.error(`فشل رفع المستند ${d.fileName}`);
          }
        }
        if (ok > 0) toast.success(`✅ تم رفع ${ok} مستند`);
      }

      toast.success('Worker added successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save data: ' + (error.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const currencySymbol = currency === 'USD' ? '$' : 'AED';

  return (
    <div className="space-y-6" data-testid="new-case-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/dashboard')}
          className="h-11 w-11 rounded-xl bg-[#004B93]/20 border border-[#004B93]/30 hover:bg-[#004B93]/40"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-white">Add New Worker</h1>
          <p className="text-sm text-white/60">Add New Worker</p>
        </div>
      </div>

      {/* Form Container */}
      <div className="max-w-3xl mx-auto space-y-6">
        {/* AI Visa Scanner Section */}
        <Card className="bg-gradient-to-br from-emerald-500/15 to-emerald-700/15 border-2 border-dashed border-emerald-500/50">
          <CardContent className="p-6 text-center">
            <Scan className="h-12 w-12 mx-auto mb-3 text-emerald-400" />
            <h3 className="text-lg font-bold text-emerald-400 mb-2">
              Scan Worker Visa
            </h3>
            <p className="text-sm text-white/70 mb-1">
              Upload visa image to auto-fill worker data
            </p>
            <p className="text-xs text-white/50 mb-4">
              AI-powered visa scanning
            </p>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf"
              onChange={handleVisaScan}
              className="hidden"
              data-testid="visa-scan-input"
            />

            {scanStatus === 'idle' && (
              <Button
                onClick={() => fileInputRef.current?.click()}
                className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 shadow-lg shadow-emerald-500/30"
                data-testid="scan-visa-btn"
              >
                <Upload className="h-4 w-4 mr-2" />
                UPLOAD VISA IMAGE
              </Button>
            )}

            {scanStatus === 'scanning' && (
              <div className="flex items-center justify-center gap-3 p-4 bg-emerald-500/10 rounded-xl">
                <Loader2 className="h-5 w-5 animate-spin text-emerald-400" />
                <span className="text-emerald-400 font-medium">
                  Scanning visa with AI...
                </span>
              </div>
            )}

            {scanStatus === 'success' && (
              <div className="flex items-center justify-center gap-2 p-3 bg-emerald-500/20 border border-emerald-500/50 rounded-xl">
                <Check className="h-5 w-5 text-emerald-400" />
                <span className="text-emerald-400 font-medium">
                  Data extracted successfully!
                </span>
              </div>
            )}

            {scanStatus === 'error' && (
              <Button
                onClick={() => {
                  setScanStatus('idle');
                  fileInputRef.current?.click();
                }}
                variant="outline"
                className="border-emerald-500/50 text-emerald-400"
              >
                <Upload className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            )}

            <p className="text-xs text-white/40 mt-4">
              Or skip and fill manually below
            </p>
          </CardContent>
        </Card>

        {/* Supplier Selection */}
        <Card className="bg-[#0D1E33] border-[#004B93]/30">
          <CardContent className="p-6">
            <Label className="text-white mb-2 block">
              Supplier <span className="text-red-500">*</span>
            </Label>
            <Select
              value={formData.supplierId}
              onValueChange={(value) => handleInputChange('supplierId', value)}
            >
              <SelectTrigger className="bg-[#0F2540] border-[#004B93]/30 text-white" data-testid="supplier-select">
                <SelectValue placeholder="Select supplier..." />
              </SelectTrigger>
              <SelectContent className="bg-[#1A2D47] border-[#004B93]/30">
                {suppliers.map(supplier => (
                  <SelectItem key={supplier.id} value={supplier.id} className="text-white">
                    {supplier.name} ({supplier.currency || 'AED'})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {suppliers.length === 0 && (
              <p className="text-xs text-white/50 mt-2">
                No suppliers. <Button variant="link" className="text-[#4DA8DA] p-0 h-auto" onClick={() => navigate('/suppliers')}>Add supplier first</Button>
              </p>
            )}
          </CardContent>
        </Card>

        {/* Worker Identity Section */}
        <Card className="bg-blue-500/10 border-blue-500/30">
          <CardHeader className="pb-4">
            <CardTitle className="text-blue-400 flex items-center gap-2">
              <User className="h-5 w-5" />
              Worker Identity
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white text-sm">
                  Full Name (English) <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={formData.workerName}
                  onChange={(e) => handleInputChange('workerName', e.target.value)}
                  placeholder="e.g., MARIA SANTOS"
                  className="bg-[#0F2540] border-[#004B93]/30 text-white mt-1"
                  data-testid="worker-name-input"
                />
              </div>
              <div>
                <Label className="text-white text-sm">Arabic Name</Label>
                <Input
                  value={formData.workerNameArabic}
                  onChange={(e) => handleInputChange('workerNameArabic', e.target.value)}
                  placeholder="Arabic name"
                  dir="rtl"
                  className="bg-[#0F2540] border-[#004B93]/30 text-white mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white text-sm">
                  Passport Number <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={formData.passportNumber}
                  onChange={(e) => handleInputChange('passportNumber', e.target.value)}
                  placeholder="e.g., E9617641"
                  className="bg-[#0F2540] border-[#004B93]/30 text-white mt-1"
                  data-testid="passport-input"
                />
              </div>
              <div>
                <Label className="text-white text-sm">
                  Nationality <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={formData.nationality}
                  onChange={(e) => handleInputChange('nationality', e.target.value)}
                  placeholder="e.g., INDONESIA"
                  className="bg-[#0F2540] border-[#004B93]/30 text-white mt-1"
                  data-testid="nationality-input"
                />
              </div>
            </div>

            {/* Passport Expiry — important for visa card display */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white text-sm">
                  Passport Expiry / تاريخ انتهاء الجواز
                </Label>
                <Input
                  type="date"
                  value={formData.passportExpiry}
                  onChange={(e) => handleInputChange('passportExpiry', e.target.value)}
                  className="bg-[#0F2540] border-[#004B93]/30 text-white mt-1"
                  data-testid="passport-expiry-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-white text-sm">Date of Birth</Label>
                <Input
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                  className="bg-[#0F2540] border-[#004B93]/30 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-white text-sm">Gender</Label>
                <Select
                  value={formData.gender}
                  onValueChange={(value) => handleInputChange('gender', value)}
                >
                  <SelectTrigger className="bg-[#0F2540] border-[#004B93]/30 text-white mt-1">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A2D47] border-[#004B93]/30">
                    <SelectItem value="Female" className="text-white">Female</SelectItem>
                    <SelectItem value="Male" className="text-white">Male</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white text-sm">Profession</Label>
                <Input
                  value={formData.profession}
                  onChange={(e) => handleInputChange('profession', e.target.value)}
                  placeholder="e.g., House Maid"
                  className="bg-[#0F2540] border-[#004B93]/30 text-white mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-white text-sm">Place of Birth</Label>
              <Input
                value={formData.placeOfBirth}
                onChange={(e) => handleInputChange('placeOfBirth', e.target.value)}
                placeholder="e.g., JAKARTA"
                className="bg-[#0F2540] border-[#004B93]/30 text-white mt-1"
              />
            </div>
          </CardContent>
        </Card>

        {/* Visa Information Section */}
        <Card className="bg-purple-500/10 border-purple-500/30">
          <CardHeader className="pb-4">
            <CardTitle className="text-purple-400 flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Visa Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white text-sm">Visa Number</Label>
                <Input
                  value={formData.visaNumber}
                  onChange={(e) => handleInputChange('visaNumber', e.target.value)}
                  placeholder="Visa Number"
                  className="bg-[#0F2540] border-[#004B93]/30 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-white text-sm">UID Number</Label>
                <Input
                  value={formData.uidNumber}
                  onChange={(e) => handleInputChange('uidNumber', e.target.value)}
                  placeholder="UID Number"
                  className="bg-[#0F2540] border-[#004B93]/30 text-white mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white text-sm">Issue Date</Label>
                <Input
                  type="date"
                  value={formData.visaIssueDate}
                  onChange={(e) => handleInputChange('visaIssueDate', e.target.value)}
                  className="bg-[#0F2540] border-[#004B93]/30 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-white text-sm">Expiry Date</Label>
                <Input
                  type="date"
                  value={formData.visaExpiryDate}
                  onChange={(e) => handleInputChange('visaExpiryDate', e.target.value)}
                  className="bg-[#0F2540] border-[#004B93]/30 text-white mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white text-sm">Issue Place</Label>
                <Input
                  value={formData.visaIssuePlace}
                  onChange={(e) => handleInputChange('visaIssuePlace', e.target.value)}
                  placeholder="Issue Place"
                  className="bg-[#0F2540] border-[#004B93]/30 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-white text-sm">Visa Type</Label>
                <Input
                  value={formData.visaType}
                  onChange={(e) => handleInputChange('visaType', e.target.value)}
                  placeholder="Visa Type"
                  className="bg-[#0F2540] border-[#004B93]/30 text-white mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-white text-sm">Sponsor Name</Label>
              <Input
                value={formData.sponsorName}
                onChange={(e) => handleInputChange('sponsorName', e.target.value)}
                placeholder="Sponsor Name"
                className="bg-[#0F2540] border-[#004B93]/30 text-white mt-1"
              />
            </div>
          </CardContent>
        </Card>

        {/* Financial Section */}
        <Card className="bg-amber-500/10 border-amber-500/30">
          <CardHeader className="pb-4">
            <CardTitle className="text-amber-400 flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Financial Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white text-sm">
                  Total Cost ({currency}) <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="number"
                  value={formData.totalCost}
                  onChange={(e) => handleInputChange('totalCost', e.target.value)}
                  placeholder={`0 ${currencySymbol}`}
                  className="bg-[#0F2540] border-[#004B93]/30 text-white mt-1"
                  data-testid="total-cost-input"
                />
              </div>
              <div>
                <Label className="text-white text-sm">Advance Payment ({currency})</Label>
                <Input
                  type="number"
                  value={formData.advanceAmount}
                  onChange={(e) => handleInputChange('advanceAmount', e.target.value)}
                  placeholder={`0 ${currencySymbol}`}
                  className="bg-[#0F2540] border-[#004B93]/30 text-white mt-1"
                />
              </div>
            </div>

            {formData.totalCost && (
              <div className="p-4 bg-[#0F2540] rounded-xl">
                <div className="flex justify-between text-sm">
                  <span className="text-white/60">Remaining:</span>
                  <span className="text-amber-400 font-bold">
                    {((parseFloat(formData.totalCost) || 0) - (parseFloat(formData.advanceAmount) || 0)).toLocaleString()} {currencySymbol}
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Photo & Status Section */}
        <Card className="bg-[#0D1E33] border-[#004B93]/30">
          <CardContent className="p-6 space-y-4">
            {/* Photo Upload */}
            <div>
              <Label className="text-white text-sm mb-2 block">Worker Photo</Label>
              <div className="flex items-center gap-4">
                <div 
                  className="w-20 h-20 rounded-xl border-2 border-dashed border-[#004B93]/50 flex items-center justify-center cursor-pointer hover:border-[#D35400] transition-colors overflow-hidden"
                  onClick={() => photoInputRef.current?.click()}
                >
                  {photoPreview ? (
                    <img src={photoPreview} alt="Worker" className="w-full h-full object-cover" />
                  ) : (
                    <Camera className="h-8 w-8 text-white/30" />
                  )}
                </div>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => photoInputRef.current?.click()}
                  className="border-[#004B93]/50 text-white"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Photo
                </Button>
              </div>
            </div>

            {/* Arrival Status */}
            <div>
              <Label className="text-white text-sm">Arrival Status</Label>
              <Select
                value={formData.arrivalStatus}
                onValueChange={(value) => handleInputChange('arrivalStatus', value)}
              >
                <SelectTrigger className="bg-[#0F2540] border-[#004B93]/30 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A2D47] border-[#004B93]/30">
                  <SelectItem value="abroad" className="text-white">Abroad</SelectItem>
                  <SelectItem value="arrived" className="text-white">Arrived</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-white text-sm">Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Additional notes..."
                className="bg-[#0F2540] border-[#004B93]/30 text-white mt-1 min-h-[100px]"
              />
            </div>
          </CardContent>
        </Card>

        {/* Documents Section (images + PDF) */}
        <Card className="bg-[#0D1E33] border-[#004B93]/30" data-testid="new-case-documents-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-orange-400 flex items-center gap-2 text-base">
              <Paperclip className="h-5 w-5" />
              المستندات / Documents
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-white/50">
              أرفق صور أو ملفات PDF (جواز، عقد، ...). تظهر في قسم الفيزا وتُحذف تلقائياً عند حذف الفيزا.
            </p>
            <input
              ref={docsInputRef}
              type="file"
              accept="image/*,.pdf,application/pdf"
              multiple
              onChange={handleDocsSelect}
              className="hidden"
              data-testid="new-case-documents-input"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => docsInputRef.current?.click()}
              className="border-[#004B93]/50 text-white"
              data-testid="new-case-add-documents-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              إضافة مستندات / Add Documents
            </Button>

            {documents.length > 0 && (
              <div className="space-y-2 pt-2">
                {documents.map((d, i) => (
                  <div
                    key={`${d.fileName}-${i}`}
                    className="flex items-center gap-3 p-2.5 rounded-xl bg-[#0F2540] border border-white/5"
                    data-testid={`new-case-document-${i}`}
                  >
                    <FileText className={`h-4 w-4 flex-shrink-0 ${d.mimeType === 'application/pdf' ? 'text-red-400' : 'text-blue-400'}`} />
                    <span className="text-white text-sm truncate flex-1">{d.fileName}</span>
                    <button
                      type="button"
                      onClick={() => removeDocument(i)}
                      className="h-8 w-8 rounded-lg flex items-center justify-center text-red-400 hover:bg-red-500/10"
                      data-testid={`remove-new-case-document-${i}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full h-14 bg-gradient-to-r from-[#D35400] to-[#A04000] hover:opacity-90 text-lg font-bold shadow-lg shadow-[#D35400]/30"
          data-testid="save-case-btn"
        >
          {saving ? (
            <>
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Check className="h-5 w-5 mr-2" />
              SAVE WORKER
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default NewCasePage;
